<template>
  <div class="wizard-step-2">
    <div class="row d-flex justify-content-center">
      <div class="px-4">
        <div class="d-flex justify-content-center">
          <image-upload @on-change="onLogoChange"
                        :name="model.title"
                        :circle="false"
                        :src="model.logo">
          </image-upload>
        </div>
      </div>
      <div class="flex-1 pr-3">
        <fg-input :name="$t('generic.title')"
                  required
                  autocomplete="newpassword"
                  :label="$t('generic.title')"
                  :placeholder="$t('generic.title')"
                  v-model="model.title"
                  v-validate="validations.title"
                  :error="getError($t('generic.title'))"
                  addon-left-icon="nc-icon nc-caps-small">
        </fg-input>

        <fg-input :label="$t('generic.language')">
          <language-select v-model="model.locale"></language-select>
        </fg-input>

        <fg-input :label="$t('job.create.details.organization')"
                  :placeholder="$t('job.create.details.organization')"
                  :error="getError($t('job.create.details.organization'))"
                  required>
          <organization-autocomplete :organization="selectedOrganization"
                                     allow-create
                                     :disabled="!getJobSetting('job_organisation_create_new')"
                                     @change="onOrganizationChange"
                                     :name="$t('job.create.details.organization')"
                                     v-validate="validations.organisation_id">
          </organization-autocomplete>
          <small slot="infoBlock">{{$t('job.create.details.organization.infoBlock')}}</small>
        </fg-input>
        <fg-input v-if="getJobSetting('job_organisation_create_new')"
                  required
                  autocomplete="off"
                  type="search"
                  :name="$t('job.create.details.organizationName')"
                  :label="$t('job.create.details.organizationName')"
                  :placeholder="$t('job.create.details.organizationName')"
                  v-model="model.organisation_name"
                  v-validate="validations.organisation_name"
                  :error="getError($t('job.create.details.organizationName'))"
                  addon-left-icon="nc-icon nc-briefcase-24">
        </fg-input>
        <fg-input :label="$t('generic.client')"
                  v-if="job.uuid"
                  :error="getError($t('generic.client'))"
                  required>
          <client-autocomplete :client="model.client"
                               :name="$t('generic.client')"
                               :clearable="false"
                               v-validate="'required|not_empty_object'"
                               @change="onClientChange">
          </client-autocomplete>
          <small slot="infoBlock">{{$t('job.create.details.client.infoBlock')}}</small>
        </fg-input>
        <fg-input
          v-if="job.uuid"
          :label="$t('generic.product')"
          :error="getError($t('generic.product'))"
          required>
          <product-select
            :options="jobPostingProducts"
            :name="$t('generic.product')"
            value-key="product_id"
            v-model="model.product_id"
            v-validate="'required'">
          </product-select>
        </fg-input>
      </div>
      <div class="col-12">
        <section-separator has-border size="lg" class="my-0">
          {{$t('job.create.details.countryLocation')}}
        </section-separator>
      </div>
      <div class="col-12 mt-3">
        <div class="row">

          <div class="col-12">
            <fg-input :error="getError($t('generic.country'))"
                      required
                      :label="$t('generic.country')">
              <country-autocomplete :name="$t('generic.country')"
                                    set-default-country
                                    :class="{'has-danger': getError($t('generic.country'))}"
                                    v-validate="validations.country_code"
                                    :value="model.country_code"
                                    @change="onCountryChange">
              </country-autocomplete>
            </fg-input>
          </div>
          <div class="col-12">
            <label class="input-label">{{$t('job.create.details.locationType')}}</label>
            <div class="radio-wrapper">
              <base-radio inline v-model="model.location_type" label="city">{{$t('job.create.details.fixedLocation')}}
              </base-radio>
              <base-radio inline v-model="model.location_type" label="country">
                {{$t('job.create.details.noFixedLocation')}}
              </base-radio>
            </div>
          </div>
          <div class="col-12 flex" v-if="model.location_type === 'city'" key="city">
            <fg-input :placeholder="$t('generic.city')"
                      required
                      :label="$t('generic.city')"
                      id="city-locality"
                      :enable-geolocation="true"
                      component="vue-google-autocomplete"
                      autocomplete="no"
                      name="job city"
                      class="flex-1"
                      :value="model.city"
                      v-validate="validations.city"
                      :error="getError('job city')"
                      :country="autoCompleteCountries"
                      @placechanged="onCityChange"
                      @input="onCityValueChange"
                      types="(cities)"
                      addon-left-icon="nc-icon nc-pin-3">
            </fg-input>

            <fg-input :placeholder="$t('generic.zipCode')"
                      :name="$t('generic.zipCode')"
                      v-model="model.postal_code"
                      :label="$t('generic.zipCode')"
                      :error="getError($t('generic.zipCode'))"
                      autocomplete="no"
                      id="city-zip"
                      class="ml-2">
            </fg-input>

          </div>
          <div class="col-12 col-sm-5" key="from-date">
            <fg-input :placeholder="$t('job.create.details.startDate')"
                      :label="$t('job.create.details.startDate')"
                      :name="$t('job.create.details.startDate')"
                      :add-form-control-class="false"
                      :error="getError($t('job.create.details.startDate'))"
                      :picker-options="startDateOptions"
                      required
                      component="el-date-picker"
                      v-validate="validations.required"
                      v-model="model.from"
                      value-format="yyyy-MM-dd HH:mm:ss"
                      @input="onStartDateChanged">
            </fg-input>
          </div>
          <div class="col-12 offset-sm-2 col-sm-5" key="till-date">
            <fg-input :placeholder="$t('job.create.details.endDate')"
                      :label="$t('job.create.details.endDate')"
                      :name="$t('job.create.details.endDate')"
                      :add-form-control-class="false"
                      :error="getError($t('job.create.details.endDate'))"
                      :picker-options="endDateOptions"
                      required
                      component="el-date-picker"
                      v-validate="validations.required"
                      value-format="yyyy-MM-dd HH:mm:ss"
                      v-model="model.till">
            </fg-input>
          </div>
          <div class="col-12">
            <section-separator has-border size="lg" class="mt-2">
              {{$t('job.create.details.applyDetails')}}
            </section-separator>
          </div>
          <div class="col-12">
            <fg-input :label="$t('job.create.details.howToApply')">
              <div class="radio-wrapper">
                <base-radio inline v-model="model.apply_type" label="jobBoard">{{$t('job.create.details.jobBoard')}}
                </base-radio>
                <base-radio inline v-model="model.apply_type" label="careerPage">{{$t('job.create.details.careerPage')}}
                </base-radio>
                <base-radio v-if="allowHidingApplyButton" inline v-model="model.apply_type" label="noApply">{{$t('job.create.details.noApply')}}
                </base-radio>
              </div>
            </fg-input>
          </div>
          <div class="col-12" v-if="model.apply_type === 'jobBoard'">
            <fg-input :required="!allowHidingApplyButton"
                      key="apply_email"
                      autocomplete="no"
                      :name="$t('job.create.details.jobBoard')"
                      :label="$t('job.create.details.jobBoard')"
                      :placeholder="$t('job.create.details.jobBoard')"
                      v-model="model.apply_email"
                      v-validate="validations.apply_email"
                      :error="getError($t('job.create.details.jobBoard'))">
            </fg-input>
          </div>
          <div class="col-12" v-if="model.apply_type === 'careerPage'">
            <fg-input :required="!allowHidingApplyButton"
                      key="apply_url"
                      :name="$t('job.create.details.careerPage')"
                      :label="$t('job.create.details.careerPage')"
                      :placeholder="$t('job.create.details.careerPage')"
                      v-model="model.apply_url"
                      v-validate="validations.apply_url"
                      :error="getError($t('job.create.details.careerPage'))">
            </fg-input>
          </div>
          <div class="col-12">
            <section-separator has-border size="lg" class="mt-2">
              {{$t('job.create.details.description')}}
            </section-separator>
          </div>
          <div class="col-12" v-if="!getJobSetting('job_field_text_blocks')">
            <fg-input :label="$t('job.create.details.textType')">
              <div class="radio-wrapper">
                <base-radio inline v-model="model.text_type" :label="textTypes.platform">
                  {{$t('job.create.details.text_type_platform')}}
                </base-radio>
                <base-radio inline v-model="model.text_type" :label="textTypes.own">
                  {{$t('job.create.details.text_type_own')}}
                </base-radio>
              </div>
            </fg-input>
          </div>
          <div class="col-12">
            <fg-input :label="$t('job.create.details.description')"
                      :required="getJobSetting('job_field_show_description')"
                      :error="getError($t('job.create.details.description'))">
              <textarea :name="$t('job.create.details.description')"
                        class="form-control"
                        :placeholder="$t('job.create.details.description')"
                        v-model="model.description"
                        v-validate="validations.description">
              </textarea>
              <div v-if="!getError($t('job.create.details.description'))" slot="helpBlock" class="mt-1" :class="{'text-danger': descriptionCharsRemaining <= 0}">
                {{$t('generic.charactersRemaining', {count: descriptionCharsRemaining})}}
              </div>
            </fg-input>
          </div>
          <template v-if="model.text_type === textTypes.own">
            <div class="col-12 mt-3">
              <fg-input :label="$t('job.create.details.jobText')"
                        required
                        key="job-text-full"
                        :error="getError($t('job.create.details.jobText'))">
                <wysiwyg :name="$t('job.create.details.jobText')"
                         class="form-control"
                         :class="{'has-danger': getError($t('job.create.details.jobText'))}"
                         v-validate="validations.required"
                         v-model="model.text_own">
                </wysiwyg>
              </fg-input>
            </div>
          </template>
          <template v-else>
            <div class="col-12 mt-3" v-if="model.text_platform">
              <fg-input :label="$t('job.create.details.jobText')"
                        required
                        key="job-text"
                        :error="getError($t('job.create.details.jobText'))">
                <wysiwyg :name="$t('job.create.details.jobText')"
                         class="form-control"
                         :class="{'has-danger': getError($t('job.create.details.jobText'))}"
                         v-validate="validations.required"
                         v-model="model.text_platform.job">
                </wysiwyg>
              </fg-input>
            </div>

            <div class="col-12 mt-3" v-if="model.text_platform">
              <fg-input :label="$t('job.create.details.functionDescription')"
                        required
                        key="function-description"
                        :error="getError($t('job.create.details.functionDescription'))">
                <wysiwyg :name="$t('job.create.details.functionDescription')"
                         class="form-control"
                         :class="{'has-danger': getError($t('job.create.details.functionDescription'))}"
                         v-validate="validations.required"
                         v-model="model.text_platform.function">
                </wysiwyg>
              </fg-input>
            </div>

            <div class="col-12 mt-3" v-if="model.text_platform">
              <fg-input :label="$t('job.create.details.companyDescription')"
                        required
                        key="company-description"
                        :error="getError($t('job.create.details.companyDescription'))">
                <wysiwyg :name="$t('job.create.details.companyDescription')"
                         class="form-control"
                         :class="{'has-danger': getError($t('job.create.details.companyDescription'))}"
                         v-validate="validations.required"
                         v-model="model.text_platform.company">
                </wysiwyg>
              </fg-input>
            </div>
            <div class="col-12 mt-3" v-if="model.text_platform">
              <fg-input :label="$t('job.create.details.offer')"
                        required
                        key="offer"
                        :error="getError($t('job.create.details.offer'))">
                <wysiwyg :name="$t('job.create.details.offer')"
                         class="form-control"
                         :class="{'has-danger': getError($t('job.create.details.offer'))}"
                         v-validate="validations.required"
                         v-model="model.text_platform.offer">
                </wysiwyg>
              </fg-input>
            </div>
          </template>

          <div class="col-12" v-if="getJobSetting('job_field_show_pixel_url')">
            <fg-input key="pixel_url"
                      :name="$t('job.create.details.pixelUrl')"
                      :label="$t('job.create.details.pixelUrl')"
                      :placeholder="$t('job.create.details.pixelUrl')"
                      v-model="model.pixel_url"
                      v-validate="validations.url"
                      :error="getError($t('job.create.details.pixelUrl'))">
            </fg-input>
          </div>
          <div class="col-12" v-if="getJobSetting('job_field_show_recruiter_name')">
            <fg-input :name="$t('job.create.details.recruiterName')"
                      :label="$t('job.create.details.recruiterName')"
                      :placeholder="$t('job.create.details.recruiterName')"
                      v-model="model.recruiter_name">
            </fg-input>
          </div>
          <div class="col-12" v-if="getJobSetting('job_field_show_recruiter_phone')">
            <fg-input :error="getError($t('job.create.details.recruiterPhone'))" :label="$t('job.create.details.recruiterPhone')">
              <phone-input
                           :name="$t('job.create.details.recruiterPhone')"
                           @input-data="onPhoneChange"
                           :value="model.recruiter_phone">
              </phone-input>
            </fg-input>
          </div>
          <div class="col-12" v-if="getJobSetting('job_field_show_recruiter_email')">
            <fg-input :name="$t('job.create.details.recruiterEmail')"
                      :label="$t('job.create.details.recruiterEmail')"
                      :placeholder="$t('job.create.details.recruiterEmail')"
                      v-model="model.recruiter_email"
                      :error="getError($t('job.create.details.recruiterEmail'))">
            </fg-input>
          </div>
          <div class="col-12" v-if="getJobSetting('job_field_show_recruiter_website')">
            <fg-input :name="$t('job.create.details.recruiterWebsite')"
                      :label="$t('job.create.details.recruiterWebsite')"
                      :placeholder="$t('job.create.details.recruiterWebsite')"
                      v-validate="validations.url"
                      :error="getError($t('job.create.details.recruiterWebsite'))">
            </fg-input>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import { Upload, DatePicker } from 'element-ui';
  import Vue from 'vue';
  import Wysiwyg from 'src/components/Wysiwyg/RedactorWysiwyg';
  import VueGoogleAutocomplete from 'src/components/Autocomplete/VueGoogleAutocomplete'
  import LanguageSelect from 'src/components/LanguageSelect'
  import { FgInput } from 'src/components';
  import CountryAutocomplete from 'src/components/Autocomplete/CountryAutocomplete'
  import EmailAutocomplete from 'src/components/Autocomplete/EmailAutocomplete'
  import OrganizationAutocomplete from 'src/components/Autocomplete/OrganizationAutocomplete'
  import ClientAutocomplete from 'src/components/Autocomplete/ClientAutocomplete';
  import PhoneInput from 'src/components/PhoneInput';
  import ImageUpload from 'src/components/ImageUpload';
  import { DAY } from 'src/config/dateUtils';
  import clone from 'lodash/cloneDeep'
  import ProductSelect from "@/components/Autocomplete/ProductSelect.vue";
  import { mapGetters } from "vuex";

  Vue.use(DatePicker);
  Vue.component(VueGoogleAutocomplete.name, VueGoogleAutocomplete);

  export default {
    components: {
      ProductSelect,
      FgInput,
      Wysiwyg,
      PhoneInput,
      ImageUpload,
      LanguageSelect,
      EmailAutocomplete,
      ClientAutocomplete,
      CountryAutocomplete,
      OrganizationAutocomplete,
      [Upload.name]: Upload
    },
    props: {
      user: {
        type: Object,
        default: () => {
          return {
            image: ''
          }
        }
      },
      client: {
        type: Object,
        default: () => {
          return {
            image: ''
          }
        }
      },
      product: {
        type: Object,
        default: () => {
          return {
            job_days: 0
          }
        }
      },
      job: {
        type: Object,
        default: () => {
          return {
            title: '',
            text_platform: {}
          }
        }
      }
    },
    computed: {
      ...mapGetters('config', ['jobPostingProducts']),
      getJobSetting() {
        return (name) => {
          let setting = this.$store.getters['config/getAppSetting'](name)
          return !!(setting && setting === '1');
        }
      },
      startDateOptions() {
        let editMode = this.job.uuid;
        let startDate = this.model.from;
        let yesterday = Date.now() - DAY;
        startDate = startDate && startDate < yesterday ? startDate : yesterday
        return {
          disabledDate(date) {
            if (editMode) {
              return false
            }
            return date.getTime() < startDate;
          }
        }
      },
      endDateOptions() {
        let editMode = this.job.uuid;
        let lowerDate = this.model.from || new Date();
        if(typeof lowerDate === 'string') {
         lowerDate = new Date(lowerDate);
        }
        let days = this.product && this.product.job_days ? this.product.job_days : 30;
        let upperDate = lowerDate.getTime() + days * DAY;
        return {
          disabledDate(date) {
            if (editMode) {
              return date.getTime() < lowerDate
            }
            return date.getTime() < lowerDate || date.getTime() > upperDate;
          }
        }
      },
      autoCompleteCountries() {
        let countryCode = this.model.country_code;
        if (countryCode) {
          return [countryCode.toLowerCase()];
        }
        return []
      },
      descriptionCharsRemaining() {
        return 300 - (this.model.description ? this.model.description.length : 0)
      },
      allowHidingApplyButton() {
        return this.getJobSetting('job_allow_to_hide_apply_button')
      },
      validations() {
        return {
          title: {
            required: true,
            max: 100
          },
          description: {
            max: 300,
            required: this.getJobSetting('job_field_show_description')
          },
          text: {
            required: true
          },
          apply_email: {
            required: !this.allowHidingApplyButton,
            email: true
          },
          apply_url: {
            required: !this.allowHidingApplyButton,
            url: true
          },
          organisation_name: {
            required: true
          },
          organisation_id: {
            required: true
          },
          country_code: {
            required: true
          },
          city: {
            required: true
          },
          email: {
            email: true
          },
          url: {
            url: true
          },
          required: {
            required: true,
            min: 10
          },
          phone: {
            required: true,
            phoneNumber: true
          },
          emailNotification: {
            email: true
          }
        }
      }
    },
    data() {
      return {
        formValid: true,
        textTypes: {
          own: 'own',
          platform: 'platform'
        },
        model: {
          organisation_id: '',
          location_type: '',
          text_type: '',
          postal_code: '',
          text: {},
        },
        selectedOrganization: {},
      }
    },
    methods: {
      onLogoChange(src) {
        this.model.logo = src;
      },
      onPhoneChange({ activeCountry, number, isValid }) {
        if (activeCountry && activeCountry.iso2 && isValid) {
          this.model.recruiter_phone_country = activeCountry.iso2;
          this.model.recruiter_phone = number;
        } else {
          this.model.recruiter_phone = '';
          this.model.recruiter_phone_country = '';
        }
      },
      scrollTop() {
        window.scrollTo(0, 0)
      },
      async validate() {
        let res = await this.$validator.validateAll();
        this.$emit('on-validated', this.model);
        this.formValid = res;
        if (!res) {
          this.scrollTop()
          this.$notify({
            type: 'danger',
            message: this.$t('job.create.details.validationMessage')
          })
        }
        return res;
      },
      onCityChange({ locality, latitude, longitude, administrative_area_level_1, country }) {
        if(latitude) {
          this.model.lat = latitude.toFixed(6);
        }
        if(longitude) {
          this.model.lon = longitude.toFixed(6);
        }
        this.model.city = locality || `${country} ${administrative_area_level_1}`;
      },
      onCityValueChange(value) {
        this.model.city = value
      },
      onCountryChange (countryCode) {
        this.model.country_code = countryCode
      },
      onStartDateChanged(val) {
        if (!val) return;
        let tillTimestamp = Date.parse(this.model.till)
        let valTimestamp = Date.parse(val)
        if (isNaN(valTimestamp)) {
          return;
        }
        if (!isNaN(tillTimestamp) && tillTimestamp < valTimestamp) {
          this.model.till = new Date(valTimestamp + DAY);
        }
      },
      onOrganizationChange(value) {
        if (value && value.name) {
          this.model.organisation_id = value.id
          this.model.organisation_name = value.name
          this.selectedOrganization = value
        } else {
          this.model.organisation_id = null
          this.model.organisation_name = null
          this.selectedOrganization = {}
        }
      },
      onClientChange(val) {
        this.model.client = val
        this.model.client_uuid = val.uuid
      }
    },
    mounted() {
      this.$watch('job', newJob => {
        let applyType = newJob.apply_type;
        if(!applyType) {
          applyType = 'noApply'
          if (newJob.apply_url) {
            applyType = 'careerPage'
          }
          if (newJob.apply_email) {
            applyType = 'jobBoard'
          }
        }
        this.model = {
          ...clone(this.model),
          ...clone(newJob),
          country_code: newJob.country_code || this.model.country_code,
          text_type: this.model.text_type || newJob.text_type,
          apply_type: applyType,
          location_type: newJob.remote ? 'country' : 'city'
        };
        this.model.locale = this.model.locale || this.$i18n.locale
        this.model.logo = this.model.logo || this.client.image || this.user.image
        this.model.organisation_name = this.model.organisation_name || this.client.name || '';
        this.selectedOrganization = {
          name: this.model.organisation_name
        }
      }, { immediate: true, deep: true });

      this.$watch('client', newClient => {
        this.model.logo = newClient.logo;
        this.model.organisation_name = newClient.name;
      }, { immediate: true });
    },
    watch: {
      'model.location_type': function(newVal) {
        this.model.remote = newVal === 'country';
        if(this.model.remote) {
          this.model.city = null;
          this.model.lat = null;
          this.model.lon = null;
        }
      },
      'model.apply_type'(newVal) {
        if (newVal === 'noApply') {
          this.model.apply_email = null
          this.model.apply_url = null
        }
      }
    }
  }
</script>
<style lang="scss">
  .wizard-step-2 {
    .el-upload.el-upload--text,
    .picture-src {
      height: 100%;
    }
    .radio-wrapper {
      display: flex;
      align-items: center;
      padding-bottom: 5px;
      padding-top: 5px;
    }
    .flex-1 {
      flex: 1;
    }
  }
</style>
